import { ReactComponent as ApiSproketIcon } from 'assets/code-sprocket-icon.svg';
import { ReactComponent as DeveloperPortalIcon } from 'assets/developer-portal-icon.svg';
import { sortByDisplayName } from 'helper-utils';
import { APIProductListSummary } from 'proto/web_app_grpc_pb';
import * as React from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { useGetCurrentUser, useGetPortalSummary, useListAPIProducts } from '../api/hooks';
import { APICatalogSearch } from './APICatalogSearch';
import { APISummaryCard } from './APISummaryCard';
import { Card } from './Card';
import { Loading } from './Loading';

export const secondsToString = (seconds: number | undefined): string => {
  if (!seconds) {
    return '';
  }
  const d = new Date(1970, 0, 1);
  d.setSeconds(seconds);
  return d.toLocaleString();
};

// The URL search param for the search filter object.
const searchFilterUrlSearchParam = 's';

interface SearchFilter {
  textFilter: string;
  apiSpecFilter: Set<string>;
  environmentFilter: Set<string>;
  tagFilter: Set<string>;
}

export const ApisCatalogPage = () => {
  const { apiCatalogInfo, apiCatalogInfoError } = useListAPIProducts();
  const { currentUser } = useGetCurrentUser();

  // This gets the search filter in the URL for browser based searching.
  const history = useHistory();
  const location = useLocation();
  const getUrlSearchFilter = (): SearchFilter => {
    // Try to get the search filters from the URL.
    // Using window.location.search since react-router's location.search isn't always the latest.
    const searchParams = new URLSearchParams(window.location.search);
    const searchFilterString = searchParams.get(searchFilterUrlSearchParam);
    if (searchFilterString) {
      // If the search filter was in the URL, then try to get the filter.
      try {
        return JSON.parse(decodeURIComponent(searchFilterString), (_key, value) =>
          Array.isArray(value) ? new Set(value) : value
        );
      } catch (e) {
        console.warn('Could not parse search filter from URL.');
      }
    }
    // Otherwise return the empty/default filter
    return {
      textFilter: '',
      apiSpecFilter: new Set<string>(),
      environmentFilter: new Set<string>(),
      tagFilter: new Set<string>()
    };
  };

  const [searchFilter, setSearchFilter] = React.useState(getUrlSearchFilter());
  const [shouldPushNextFilterChangeToHistory, setShouldPushNextFilterChangeToHistory] = React.useState(false);

  // Update the URL after a delay, when the searchFilter changes.
  const [searchParamsTimeout, setSearchParamsTimeout] = React.useState<NodeJS.Timeout | null>(null);
  React.useEffect(() => {
    const newSearchParamsTimeout = setTimeout(() => {
      // Update the search filter in the URL for browser based searching.
      const newSearchParams = new URLSearchParams(window.location.search);
      const newSearchFilterString = JSON.stringify(searchFilter, (_key, value) =>
        value instanceof Set ? Array.from(value) : value
      );
      newSearchParams.set(searchFilterUrlSearchParam, encodeURIComponent(newSearchFilterString));
      const newSearchParamsString = `?${newSearchParams.toString()}`;
      if (window.location.search === newSearchParamsString) {
        // Stop here if there were no changes to the search params.
        return;
      }
      // Otherwise update the URL.
      const newHistoryState = {
        pathname: location.pathname,
        search: newSearchParamsString
      };
      if (shouldPushNextFilterChangeToHistory) {
        // Push this URL on to the history stack, so it is saved in browser history.
        history.push(newHistoryState);
      } else {
        // Keep the URL updated by replacing it, but don't push to it.
        history.replace(newHistoryState);
      }
    }, 300);
    setSearchParamsTimeout(newSearchParamsTimeout);
    return () => {
      clearTimeout(newSearchParamsTimeout);
    };
  }, [
    searchFilter.apiSpecFilter,
    searchFilter.environmentFilter,
    searchFilter.tagFilter,
    searchFilter.textFilter,
    shouldPushNextFilterChangeToHistory
  ]);

  // When navigating, update the searchFilter object accordingly.
  React.useEffect(() => {
    const onNavigate = () => {
      if (searchParamsTimeout !== null) {
        clearTimeout(searchParamsTimeout);
      }
      const newSearchFilter = getUrlSearchFilter();
      setSearchFilter(newSearchFilter);
      // Do not push this next filter change to history.
      setShouldPushNextFilterChangeToHistory(false);
    };
    window.addEventListener('popstate', onNavigate);
    return () => {
      window.removeEventListener('popstate', onNavigate);
    };
  }, [searchParamsTimeout]);

  const { portalSummary } = useGetPortalSummary();
  React.useEffect(() => {
    document.title = (portalSummary?.displayName ?? 'Portal') + ' - APIs';
  }, [portalSummary?.displayName]);

  const filterApiProducts = (apiInfo: APIProductListSummary.AsObject) =>
    !searchFilter.textFilter.length ||
    apiInfo.displayName?.toLowerCase().includes(searchFilter.textFilter.toLowerCase()) ||
    apiInfo.description?.toLowerCase().includes(searchFilter.textFilter.toLowerCase());

  if (!apiCatalogInfo && !apiCatalogInfoError) {
    return <Loading message='Loading...' />;
  }

  return (
    <main className='h-full bg-gray-100 apis-catalog-container '>
      <div className='background-image-holder'>
        <img src={`/portal/banner`} alt={''} />
      </div>
      <div className='px-20 py-10 bg-cool-gray-600 apis-catalog-headings '>
        <div className='flex items-center px-4 mx-auto space-x-4 apis-catalog-title max-w-7xl sm:px-6 lg:px-8'>
          <h1 className='text-4xl font-normal text-white'>APIs</h1>
          <ApiSproketIcon className='fill-current' title='' />
        </div>
        <div className='px-4 mx-auto mt-2 apis-catalog-description-container max-w-7xl sm:px-6 lg:px-8'>
          <div className='text-white apis-catalog-description'>
            Browse the list of APIs and documentation in this portal. From here you can get the information you need to
            make API calls.
          </div>
        </div>
      </div>
      <div className='grid grid-cols-4 gap-5 mx-auto mt-4 apis-catalog-list-container max-w-7xl sm:px-6 lg:px-8'>
        {!!apiCatalogInfo?.apiProductsList?.length && (
          <div className='col-span-1 '>
            <APICatalogSearch
              currentFilter={searchFilter}
              updateFilter={newSearchFilter => {
                // Do push this next filter change to history.
                setShouldPushNextFilterChangeToHistory(true);
                setSearchFilter(newSearchFilter);
              }}
              apiTypes={apiCatalogInfo?.searchInfo?.apiTypesList ?? []}
              environments={apiCatalogInfo?.searchInfo?.environmentsList ?? []}
              tags={apiCatalogInfo?.searchInfo?.tagsList ?? []}
            />
          </div>
        )}
        <div className='col-span-3 space-y-6 apis-list'>
          {!!apiCatalogInfo?.apiProductsList?.length ? (
            apiCatalogInfo?.apiProductsList
              ?.filter(filterApiProducts)
              .sort(sortByDisplayName)
              .map(apiInfo => (
                <APISummaryCard
                  key={apiInfo?.metadata?.uid}
                  content={{
                    name: apiInfo?.metadata?.name ?? '',
                    namespace: apiInfo?.metadata?.namespace ?? '',
                    modifiedDateTime: !!apiInfo?.modifiedDate
                      ? secondsToString(apiInfo.modifiedDate.seconds)
                      : 'Unknown',
                    displayName: apiInfo?.displayName ?? '',
                    description: apiInfo?.description ?? '',
                    versions: apiInfo?.versionsList ?? [],
                    imageUrl: apiInfo?.imageUrl ?? ''
                  }}
                  searchFilter={searchFilter}
                />
              ))
              .filter(card => card !== null)
          ) : (
            <>
              <Card>
                <div className='empty-card'>
                  <div className='empty-card-image-holder'>
                    <DeveloperPortalIcon title='' />
                  </div>
                  <div className='empty-card-text'>
                    <div className='empty-card-text-heading'>There are no APIs to display.</div>
                    {!!currentUser || !!apiCatalogInfo?.portalAllowAll ? (
                      <div>If you were expecting APIs here check with your admin.</div>
                    ) : (
                      <div>
                        You need to log in to see the APIs here. <NavLink to='/login/'>Log in now.</NavLink>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
              <div className='empty-list-screen' />
            </>
          )}
        </div>
      </div>
    </main>
  );
};
